import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { createPortal } from 'react-dom';

import Button from '@/src/components/common/Elements/Button/Button';
import Icon, { ICONS, ICON_TYPES } from '@/src/components/common/Elements/Icon/Icon';
import Tooltip from '@/src/components/common/Elements/Tooltip/Tooltip';
import DocumentLibraryModal from '@/src/components/project/Common/Modals/DocumentLibraryModal/DocumentLibraryModal';
import XHRUploader from '@/src/components/shared/XHRUploader.js';
import { showToast, maxFileSize } from '@/src/globalFunction';
import { useToggle } from '@/src/hooks/useToggle';
import { cn } from '@/src/util/ui';

import CancelUpload from './CancelUpload/CancelUpload';

import { UPLOAD_SOURCES } from '../../../Config/inbox.config';

let cxhrs = [];
let totalSize = 0;

const formatLibraryDocument = (document, index) => {
  const fileObject = {
    ...document,
    name: document.origin,
  };
  const documentObject = {
    ...document,
    filename: document.name,
    source: UPLOAD_SOURCES.LIBRARY,
  };

  return {
    cancelled: false,
    doc: documentObject,
    file: fileObject,
    index: index,
    progress: 100,
    remaining: 0,
    msg: '',
    replace: false,
    source: UPLOAD_SOURCES.LIBRARY,
  };
};

class Uploader extends XHRUploader {
  constructor(props) {
    super(props);
    this.fileInput = React.createRef();
  }

  componentDidUpdate(prevProps, prevState) {
    const { onUpload, onUploading, showUploadLocal, toggleShowUploadLocal } = this.props;
    const { items } = this.state;

    if (!_.isEqual(prevState.items, items)) {
      onUpload(items.filter((document) => document.cancelled == false).map((item) => item.doc));
      if (items.filter((item) => !item.cancelled && item.progress < 100).length) {
        return;
      }
      onUploading(false);
    }

    if (showUploadLocal) {
      this.fileInput.current.click();
      toggleShowUploadLocal(false);
    }
  }

  onFileSelect = () => {
    let items = this.filesToItems(this.fileInput.current.files);
    items = [...this.state.items, ...items];

    setTimeout(() => {
      this.fileInput.current.value = '';
      this.setState({ items, completed: false }, () => {
        if (this.props.auto) {
          this.upload();
        }
      });
    }, 500);
  };

  fileCompleted = () => {
    const { items } = this.state;
    const completed = items.filter((item) => item.progress === 100).length;
    const notError = items.filter((item) => !item.error).length;

    if (completed === notError) {
      setTimeout(() => {
        showToast('success', `${items.length > 1 ? 'Files' : 'File'} successfully uploaded.`);
      }, 1000);
    }
  };

  updateFileProgress = (index, data) => {
    const newItems = [...this.state.items];
    const progress = data.percent;
    const remaining = data.remaining;
    const speed = data.speed;
    const doc = {
      ...data.doc,
      source: UPLOAD_SOURCES.LOCAL,
    };
    newItems[index] = Object.assign({}, this.state.items[index], { progress, remaining, doc });

    this.setState({ items: newItems, speed }, this.fileCompleted);
  };

  filesToItems = (files) => {
    const fileItems = Array.prototype.slice.call(files).slice(0, this.props.maxFiles);
    let index = this.state.items.length;
    const items = fileItems.map((f) => {
      if (this.props.chunks) {
        const chunkProgress = [];
        for (let j = 0; j <= f.size / this.props.chunkSize; j += 1) {
          chunkProgress.push(0);
        }
        return {
          file: f,
          index: index++,
          progress: 0,
          cancelled: false,
          chunkProgress,
          remaining: 0,
          doc: {},
          replace: false,
          msg: '',
        };
      }

      let item = {
        file: f,
        index: index++,
        progress: 0,
        cancelled: false,
        remaining: 0,
        doc: {},
        replace: false,
        msg: '',
      };
      item = this.checkFileSupported(item);

      if (!item.msg) {
        item = this.handleMaxFileSize(item);
      }

      return {
        ...item,
        source: UPLOAD_SOURCES.LOCAL,
      };
    });

    return items;
  };

  handleMaxFileSize = (item) => {
    const max = maxFileSize();
    const fileSize = item.file.size || 0;
    const tempTotalSize = totalSize + fileSize;
    const isExceed = tempTotalSize > max;
    const isExceedTotalSize = tempTotalSize > max;

    if (isExceed) {
      item.error = true;
      item.msg = 'File is more than 25mb';

      showToast('error', 'File size exceeds 25MB');
    } else if (isExceedTotalSize) {
      item.error = true;
      item.msg = `Batch File is more than ${Math.floor(max / (1024 * 1024))}mb`;

      showToast('error', 'Batch file size exceeds 25MB');
    } else {
      totalSize += fileSize;
    }

    return item;
  };

  checkFileSupported = (item) => {
    let file = item.file;
    let fileExtension = file.name.split('.').pop();
    let index = this.props.accept.split(',').indexOf('.' + fileExtension.toLowerCase());

    if (index < 0) {
      item.error = true;
      item.msg = 'Invalid File Format';
    }
    return item;
  };

  upload = () => {
    const { onUploading } = this.props;
    const { items } = this.state;
    let newItems = [...items];

    if (newItems) {
      onUploading(true);
      newItems
        .filter((item) => !item.cancelled && !item.error && item.progress === 0)
        .forEach((item) => {
          this.uploadItem(item);
        });
    }
  };

  uploadItem = (item) => {
    if (this.props.chunks && item.file) {
      const BYTES_PER_CHUNK = this.props.chunkSize;
      const SIZE = item.file.size;

      let start = 0;
      let end = BYTES_PER_CHUNK;

      const chunkProgressHandler = (percentage, chunkIndex) => {
        this.updateFileChunkProgress(item.index, chunkIndex, percentage);
      };

      let chunkIndex = 0;
      while (start < SIZE) {
        this.uploadChunk(
          item.file.slice(start, end),
          (chunkIndex += 1),
          item.file.name,
          chunkProgressHandler
        );
        start = end;
        end = start + BYTES_PER_CHUNK;
      }
    } else {
      this.uploadFile(item, (progress) => this.updateFileProgress(item.index, progress));
    }
  };

  cancelFile = () => {
    const { toggleShowCancelModal, onUpload } = this.props;
    const index = this.index;
    const newItems = [...this.state.items];
    const cancelledItem = newItems[index];

    if (index > -1) {
      if (!cancelledItem.error) {
        totalSize -= cancelledItem.file.size;
      }

      if (this.xhrs[index]) {
        this.xhrs[index].upload.removeEventListener('progress', () => {
          console.log('remove');
        });
        this.xhrs[index].removeEventListener('load', () => {
          console.log('remove');
        });
        this.xhrs[index].abort();
      }

      this.index = -1;
      newItems[index] = Object.assign({}, this.state.items[index], {
        cancelled: true,
        progress: 0,
        msg: 'Upload cancelled',
      });
      this.setState({ items: newItems });

      onUpload(this.getUploadedFiles({ newItems }));
    }

    toggleShowCancelModal(false);
  };

  getUploadedFiles = ({ newItems, filesToAdd = [], source } = {}) => {
    const items = newItems ? newItems : this.state.items;
    const uploadedFiles = items
      .filter((document) =>
        source
          ? document.progress === 100 && document.cancelled == false && document.source === source
          : document.progress === 100 && document.cancelled == false
      )
      .map((item) => item.doc);

    if (filesToAdd.length) {
      uploadedFiles.push(...filesToAdd);
    }

    return uploadedFiles;
  };

  uploadFile = (item, progressCallback) => {
    let file = item.file;

    if (file) {
      const formData = new FormData();
      const xhr = new XMLHttpRequest();
      formData.append('file', file);
      let started_at = new Date();

      xhr.onload = () => {
        const resp = JSON.parse(xhr.response);
        const doc = resp.data.results[0];

        const data = {
          percent: 100,
          speed: 0,
          remaining: 0,
          doc: doc,
        };
        progressCallback(data);
      };

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          let percent = Math.floor((e.loaded / e.total) * 99);

          let seconds_elapsed = (new Date().getTime() - started_at.getTime()) / 1000;
          let bytes_per_second = seconds_elapsed ? e.loaded / seconds_elapsed : 0;
          let Kbytes_per_second = bytes_per_second / 1000;
          let remaining_bytes = e.total - e.loaded;
          let seconds_remaining = seconds_elapsed
            ? remaining_bytes / bytes_per_second
            : 'calculating';
          const data = {
            percent,
            speed: Kbytes_per_second,
            remaining: seconds_remaining,
            id: 0,
          };
          progressCallback(data);
        }
      };
      xhr.open(this.props.method, this.props.url, true);
      xhr.send(formData);
      cxhrs[item.index] = xhr;
    }
  };

  handleUploadFromLibrary = (documentList) => {
    const { toggleShowLibraryModal } = this.props;
    const { items } = this.state;
    let index = items.length;
    const filesToAdd = documentList.map((document) => formatLibraryDocument(document, index++));
    const newItems = [...items, ...filesToAdd];

    this.setState((prevState) => ({
      ...prevState,
      items: newItems,
    }));

    showToast('success', `${documentList.length > 1 ? 'Files' : 'File'} successfully uploaded.`);
    toggleShowLibraryModal(false);
  };

  handlePromptCancelFile = (item) => {
    const { toggleShowCancelModal } = this.props;
    this.index = item.index;

    toggleShowCancelModal(true);
  };

  renderFileSet = (id) => {
    const { items } = this.state;
    const { progressClass, filesetTransitionName: transitionName } = this.props;
    let activeItem = items.filter((e) => {
      return e.cancelled == false;
    });
    const { styles } = this.state;
    let addstyle = {};

    if (items.length > 0 && activeItem.length > 0) {
      addstyle = {
        zIndex: 2,
      };
    }

    const filesetStyle = { ...styles.fileset, ...addstyle, marginTop: '0' };

    return createPortal(
      <div
        className={clsx(
          'container--uploadMain',
          'tw-w-full tw-max-h-[150px]',
          'tw-bg-white',
          'tw-overflow-y-auto tw-scroll-styles',
          'tw-ml-0',
          'tw-rounded-[10px]',
          'empty:!tw-p-0'
        )}
        id={`${id}-container--uploadFiles`}
        style={filesetStyle}
      >
        {items
          .filter((item) => !item.cancelled && !!item.file)
          .map((item) => {
            const file = item.file;
            const iconColor = item.progress < 100 ? 'tw-text-cfo-grey-600' : 'tw-text-cfo-black';

            return (
              <div
                key={item.index}
                className={clsx(
                  'wrapper--attachmentItem',
                  'tw-p-[10px] tw-mx-[10px] tw-my-[3px]',
                  'tw-border-solid tw-border-[1px] tw-border-cfo-grey-500 tw-rounded-[3px]',
                  'tw-bg-white'
                )}
              >
                <div
                  className={clsx(
                    'container--attachmentItem',
                    'tw-flex tw-items-center tw-justify-between',
                    'tw-p-0'
                  )}
                >
                  <div className={clsx('container--fileDetails', 'tw-flex tw-items-center')}>
                    <Icon
                      className={clsx('tw-mr-[5px]', 'tw-text-[22px]', iconColor)}
                      icon={ICONS.file}
                    />
                    <span className={clsx(iconColor)}>{`${file.name.length > 25 ? file.name.substr(0, 25) + '...' : file.name
                      }`}</span>
                  </div>

                  <div
                    className={clsx(
                      'wrapper--uploadProgress',
                      'tw-flex tw-items-center tw-justify-end',
                      'tw-max-w-[333px] tw-w-full'
                    )}
                  >
                    {!item.error && (
                      <progress
                        style={
                          progressClass
                            ? {}
                            : !item.cancelled
                              ? styles.progress
                              : { ...styles.progress, backgroundColor: 'red' }
                        }
                        className={cn(
                          'container--progress',
                          '!tw-mt-0',
                          '!tw-w-full',
                          'tw-border-solid tw-border-[2px] tw-border-[#6fe983]',
                          progressClass
                        )}
                        min="0"
                        max="100"
                        value={item.progress}
                        data-tooltip-id={`${item.index}-progressBar`}
                      >
                        {item.progress}%
                      </progress>
                    )}

                    {!item.error && (
                      <Tooltip id={`${item.index}-progressBar`} place="top" effect="solid">
                        {item.progress}% uploaded
                      </Tooltip>
                    )}

                    {item.error ? (
                      <Button
                        className={clsx('tw-group', 'tw-m-0', 'tw-text-cfo-red-400')}
                        onClick={() => {
                          this.handlePromptCancelFile(item);
                        }}
                      >
                        <Icon
                          className={clsx(
                            'tw-text-[15px]',
                            'tw-border-solid tw-border-[1px] tw-border-cfo-red-400 tw-rounded-[50%]',
                            'group-hover:tw-bg-cfo-red-400 group-hover:tw-text-white'
                          )}
                          data-tooltip-id={`uploadIcon-${item.index}`}
                          data-offset="{'left': 50}"
                          icon={ICONS.warning}
                        />

                        <Tooltip
                          id={`uploadIcon-${item.index}`}
                          class={`error-tooltip ${item.msgType}`}
                          place="left"
                          effect="solid"
                        >
                          {item.msg}
                        </Tooltip>
                      </Button>
                    ) : (
                      <Button
                        className={clsx(
                          'tw-group',
                          'tw-mr-0 tw-ml-[10px]',
                          ' tw-text-cfo-black tw-leading-none',
                          'hover:tw-text-cfo-blue-700'
                        )}
                        onClick={() => {
                          this.handlePromptCancelFile(item);
                        }}
                      >
                        <Icon
                          className={clsx(
                            'tw-text-[20px] tw-rounded-[50%]',
                            'group-hover:tw-bg-cfo-blue-200'
                          )}
                          icon={ICONS.close}
                          iconType={ICON_TYPES.rounded}
                        />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
      </div>,
      document.getElementById(`${id}-attachments-upload-status-container`)
    );
  };

  render() {
    const {
      showLibraryModal,
      toggleShowLibraryModal,
      showCancelModal,
      toggleShowCancelModal,
      sourceFiles,
    } = this.props;
    const maxFiles = this.props.maxFiles;
    const accept = this.props.accept;
    const styles = {
      root: {
        padding: 0,
      },
    };
    const isHide = this.props.hide;
    const id = this.props.id ? this.props.id : 'upload';

    return (
      <React.Fragment>
        <div className={clsx('toolbar-buttons')} style={styles.root}>
          <span style={{ display: `${isHide ? 'none' : 'in-line'}` }}>{this.renderButton()}</span>

          {this.state.items.length > 0 && (
            <span className="uploadRender">{this.renderFileSet(id)}</span>
          )}

          <input
            id={`${id}`}
            ref={this.fileInput}
            style={{ display: 'none' }}
            multiple={maxFiles > 1}
            accept={accept}
            type="file"
            onChange={this.onFileSelect}
          />

          {/* {showLibraryModal && (
            <React.Fragment>
              <DocumentLibraryModal
                title={'Upload from Library'}
                onClose={toggleShowLibraryModal.bind(null, false)}
                onUpload={this.handleUploadFromLibrary}
                sourceFiles={sourceFiles}
              />
            </React.Fragment>
          )} */}

          {showCancelModal && (
            <React.Fragment>
              <CancelUpload
                onClose={toggleShowCancelModal.bind(null, false)}
                onCancel={this.cancelFile}
              />
            </React.Fragment>
          )}

          <span
            id={`${id}`}
            style={{ display: 'none' }}
            onClick={() => {
              this.onClick();
            }}
            aria-hidden={true}
          />
        </div>
      </React.Fragment>
    );
  }
}

const withHooks = (Component) => (props) => {
  const [showCancelModal, toggleShowCancelModal] = useToggle(false);

  return (
    <Component
      showCancelModal={showCancelModal}
      toggleShowCancelModal={toggleShowCancelModal}
      {...props}
    />
  );
};

export default withHooks(Uploader);
